import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './StaticPages.css';
import './AdvertiseWithUs.css';

const API_URL = process.env.REACT_APP_API_URL;

const AdvertiseWithUs = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    companyName: '',
    email: '',
    website: '',
    phone: '',
    plan: '6months', // or '12months'
    adImage: null,
    targetUrl: '', // Where ad should link to
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const isLoggedIn = !!localStorage.getItem('token');

  const plans = {
    '6months': { price: 29.99, duration: '6 Months', impressions: '~50,000' },
    '12months': { price: 59.99, duration: '12 Months', impressions: '~100,000' }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    // Validate file type
    if (!file.type.match('image/jpeg') && !file.type.match('image/png')) {
      setError('Please upload a JPEG or PNG image');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('Image must be under 2MB');
      return;
    }

    // Create image to check dimensions
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (event) => {
      img.src = event.target.result;

      img.onload = () => {
        // Validate dimensions (200x150 or close)
        if (Math.abs(img.width - 200) > 10 || Math.abs(img.height - 150) > 10) {
          setError('Image must be approximately 200x150 pixels');
          return;
        }

        // All validations passed
        setFormData({ ...formData, adImage: file });
        setImagePreview(event.target.result);
        setError('');
      };
    };

    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate
    if (!formData.companyName || !formData.email || !formData.adImage) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    try {
      // Create form data for file upload
      const submitData = new FormData();
      submitData.append('companyName', formData.companyName);
      submitData.append('email', formData.email);
      submitData.append('website', formData.website);
      submitData.append('phone', formData.phone);
      submitData.append('plan', formData.plan);
      submitData.append('targetUrl', formData.targetUrl);
      submitData.append('adImage', formData.adImage);

      // Submit to backend
      const response = await fetch(`${API_URL}/advertise/submit`, {
        method: 'POST',
        body: submitData,
      });

      const data = await response.json();

      if (response.ok) {
        // Redirect to Stripe Checkout
        window.location.href = data.checkoutUrl;
      } else {
        setError(data.error || 'Submission failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="static-page">
      <header className="static-header">
        <nav className="static-nav">
          <span className="logo-icon"></span>
          <div className="logo" onClick={() => navigate('/')}>Mellodai</div>
          <ul>
            <li><a onClick={() => navigate('/')}>Home</a></li>
            <li><a onClick={() => navigate('/advertise')} className="active">Advertise</a></li>
            <li><a onClick={() => navigate(isLoggedIn ? '/app' : '/login')} className="cta-button">{isLoggedIn ? 'Predictions' : 'Get Started'}</a></li>
          </ul>
        </nav>
      </header>

      <section className="advertise-hero">
        <h1>Advertise with Mellodai</h1>
        <p>Reach thousands of users interested in AI, future planning, and data-driven insights</p>
      </section>

      <section className="advertise-content">
        <div className="advertise-grid">
          {/* Left: Benefits */}
          <div className="benefits-section">
            <h2>Why Advertise With Us?</h2>

            <div className="benefit-card">
              <div className="benefit-icon"></div>
              <h3>Targeted Audience</h3>
              <p>Tech-savvy users interested in AI, predictions, career planning, and life decisions</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"></div>
              <h3>Quality Traffic</h3>
              <p>~10,000 monthly visitors with high engagement and decision-making intent</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"></div>
              <h3>Affordable Pricing</h3>
              <p>Starting at just $29.99 for 6 months of exposure</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"></div>
              <h3>Premium Placement</h3>
              <p>Your ad appears on high-traffic pages across the platform</p>
            </div>

            <h3 className="specs-title">Ad Specifications:</h3>
            <ul className="specs-list">
              <li> Dimensions: 200x150 pixels</li>
              <li> Format: JPEG or PNG</li>
              <li> Max size: 2MB</li>
              <li> Clickable with custom URL</li>
              <li> Manual review & approval</li>
            </ul>
          </div>

          {/* Right: Form */}
          <div className="form-section">
            <div className="pricing-options">
              <h3>Choose Your Plan</h3>
              <div className="plan-cards">
                {Object.entries(plans).map(([key, plan]) => (
                  <div
                    key={key}
                    className={`plan-card ${formData.plan === key ? 'selected' : ''}`}
                    onClick={() => setFormData({ ...formData, plan: key })}
                  >
                    <h4>{plan.duration}</h4>
                    <div className="plan-price">${plan.price}</div>
                    <p className="plan-impressions">{plan.impressions} impressions</p>
                    {formData.plan === key && <div className="plan-checkmark"></div>}
                  </div>
                ))}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="advertise-form">
              <h3>Submit Your Ad</h3>

              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  placeholder="Your Company"
                  required
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>Website</label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://yourcompany.com"
                />
              </div>

              <div className="form-group">
                <label>Phone (optional)</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div className="form-group">
                <label>Target URL (where ad should link) *</label>
                <input
                  type="url"
                  value={formData.targetUrl}
                  onChange={(e) => setFormData({ ...formData, targetUrl: e.target.value })}
                  placeholder="https://yourproduct.com/landing"
                  required
                />
              </div>

              <div className="form-group">
                <label>Upload Ad Image (200x150px) *</label>
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleImageUpload}
                  required
                />
                <p className="form-hint">JPEG or PNG, 200x150 pixels, max 2MB</p>
              </div>

              {imagePreview && (
                <div className="image-preview">
                  <p>Preview:</p>
                  <img src={imagePreview} alt="Ad preview" />
                </div>
              )}

              {error && (
                <div className="error-message">
                  <span className="error-icon"></span>
                  {error}
                </div>
              )}

              {success && (
                <div className="success-message">
                  <span className="success-icon"></span>
                  Submission successful! Redirecting to payment...
                </div>
              )}

              <button
                type="submit"
                className="submit-btn"
                disabled={loading || !formData.adImage}
              >
                {loading ? 'Processing...' : `Proceed to Payment ($${plans[formData.plan].price})`}
              </button>

              <p className="terms-text">
                By submitting, you agree to our <a onClick={() => navigate('/terms')}>Terms of Service</a> and <a onClick={() => navigate('/ad-policy')}>Advertising Policy</a>
              </p>
            </form>
          </div>
        </div>
      </section>

      <footer className="static-footer">
        {/* Your existing footer */}
      </footer>
    </div>
  );
};

export default AdvertiseWithUs;
